import type {
  ActionReducerMapBuilder,
  EntityState,
  PayloadAction,
} from '@reduxjs/toolkit';

import type { ImportFlowPayload } from '@polyai/stream/commands/handlers/flow';
import type { CreateFunctionPayload } from '@polyai/stream/commands/handlers/functions/create-function/schema';

import {
  isStepReferencesFunction,
  isStopKeywordReferencesFunction,
  isTopicReferencesFunction,
} from '../../matchers';
import { assignMetadata } from '../../utils/assign-metadata';
import { replaceFlowNameInGotoFlow } from '../../utils/functions-helpers';
import {
  deleteStopKeywordsReferences,
  handleCreateFlowReferences,
  handleDeleteFlowReferences,
  handleDeleteFlowStepReferences,
  handleDeleteTopicReferences,
  handleImportFlowReferences,
  handleStepReferenceUpdates,
  handleTopicReferenceUpdates,
  updateRulesReferences,
  updateStopKeywordsReferences,
} from '../../utils/reference-helpers';
import { updateRules } from '../agent-settings/actions';
import {
  createFlow,
  deleteFlow,
  deleteFlowStep,
  importFlow,
  updateFlow,
} from '../flows';
import { deleteTopic, updateTopic } from '../knowledge-base';
import { addCreatedByAndAt } from '../shared/utils';
import { deleteStopKeywords } from '../stop-keywords';
import {
  delayResponsesAdapter,
  functionsAdapter,
  functionsReferencesAdapter,
  parametersAdapter,
} from './adapters';
import { functionsSelectors } from './selectors';
import type {
  Function,
  FunctionDelayResponse,
  FunctionLatencyControl,
  FunctionParameter,
} from './types';

export const getDefaultLatencyControl = (
  createdBy: string,
  createdAt: string,
): FunctionLatencyControl =>
  addCreatedByAndAt(
    createdBy,
    createdAt,
  )({
    enabled: false,
    delayResponses: delayResponsesAdapter.getInitialState(),
    initialDelay: 0,
    interval: 0,
  });

export const createFunctionWithEntityStates = (
  action:
    | PayloadAction<CreateFunctionPayload>
    | PayloadAction<ImportFlowPayload>,
  functionPayload: CreateFunctionPayload,
): Function => {
  // Latency control
  const latencyControl: FunctionLatencyControl = {
    enabled: !!functionPayload.latencyControl?.enabled,
    delayResponses: delayResponsesAdapter.getInitialState(
      {},
      functionPayload.latencyControl?.delayResponses.map(d => {
        const newDelayResponse: FunctionDelayResponse = {
          ...d,
          createdAt: '',
          createdBy: '',
          updatedAt: '',
          updatedBy: '',
        };
        assignMetadata(action, newDelayResponse);
        return newDelayResponse;
      }),
    ),
    initialDelay: functionPayload.latencyControl?.initialDelay ?? 0,
    interval: functionPayload.latencyControl?.interval ?? 0,
    createdAt: '',
    createdBy: '',
    updatedAt: '',
    updatedBy: '',
  };

  assignMetadata(action, latencyControl);

  // Parameters
  const parameters = parametersAdapter.getInitialState(
    {},
    functionPayload.parameters.map(p => {
      const newParam: FunctionParameter = {
        ...p,
        createdAt: '',
        createdBy: '',
        updatedAt: '',
        updatedBy: '',
      };
      assignMetadata(action, newParam);
      return newParam;
    }),
  );

  // Final function output
  const newFunction: Function = {
    ...functionPayload,
    latencyControl,
    parameters,
    // Newly created function with no references
    references: functionsReferencesAdapter.createInitialState(
      functionPayload.references,
    ),
    createdAt: '',
    createdBy: '',
    updatedAt: '',
    updatedBy: '',
    archived: false,
  };

  assignMetadata(action, newFunction);

  return newFunction;
};

export function registerFunctionReducersForFlowActions(
  builder: ActionReducerMapBuilder<{
    functions: EntityState<Function, string>;
  }>,
) {
  builder.addCase(updateFlow, (state, action) => {
    const { name: newFlowName, oldFlowName } = action.payload;
    if (!newFlowName || !oldFlowName || oldFlowName === newFlowName) {
      return;
    }

    for (const func of Object.values(state.functions.entities)) {
      if (!func?.code) {
        continue;
      }
      const updatedCode = replaceFlowNameInGotoFlow(
        func.code,
        oldFlowName,
        newFlowName,
      );
      if (updatedCode !== func.code) {
        functionsAdapter.updateOne(state.functions, {
          id: func.id,
          changes: { code: updatedCode },
        });
      }
    }
  });

  // Flow is created
  builder.addCase(createFlow, (state, action) => {
    handleCreateFlowReferences(
      action.payload.steps,
      'globalFunctions',
      action.payload.id,
      functionsReferencesAdapter.addReference,
      state.functions,
    );
  });

  // Flow is imported
  builder.addCase(importFlow, (state, action) => {
    if (action.payload.globalFunctions.length) {
      functionsAdapter.addMany(
        state.functions,
        action.payload.globalFunctions.map(f =>
          createFunctionWithEntityStates(action, f),
        ),
      );
    }
    handleImportFlowReferences(
      action.payload.steps,
      'globalFunctions',
      action.payload.flowId,
      functionsReferencesAdapter.addReference,
      state.functions,
    );
  });

  // Flow is deleted
  builder.addCase(deleteFlow, (state, action) => {
    handleDeleteFlowReferences(
      functionsSelectors.selectAll(state.functions),
      action.payload.flowId,
      functionsReferencesAdapter.removeReferencePrefix,
      state.functions,
    );
  });

  // Step flow is deleted
  builder.addCase(deleteFlowStep, (state, action) => {
    handleDeleteFlowStepReferences<
      Function,
      'flowSteps' | 'topics' | 'behaviour' | 'stopKeywords' | 'variables'
    >(
      functionsSelectors.selectAll(state.functions),
      action.payload.flowId,
      action.payload.stepId,
      functionsReferencesAdapter.removeReference,
      state.functions,
    );
  });
}

export function registerFunctionReducersForTopicActions(
  builder: ActionReducerMapBuilder<{
    functions: EntityState<Function, string>;
  }>,
) {
  // Topic is deleted
  builder.addCase(deleteTopic, (state, action) => {
    handleDeleteTopicReferences(
      state.functions.entities,
      action.payload.id,
      functionsReferencesAdapter.removeReference,
      state.functions,
    );
  });

  // Topic is updated, check if references are removed
  builder.addCase(updateTopic, (state, action) => {
    const { id: topicId, references: topicReferences } = action.payload;
    const entities = state.functions.entities;
    Object.values(entities)
      .filter(
        e =>
          e.references.topics[topicId] !== undefined &&
          topicReferences?.globalFunctions?.[e.id] === undefined,
      )
      .forEach(e => {
        functionsReferencesAdapter.removeReference(
          state.functions,
          e.id,
          'topics',
          topicId,
        );
      });
  });
}

export function registerRulesOperations(
  builder: ActionReducerMapBuilder<{
    functions: EntityState<Function, string>;
  }>,
) {
  builder.addCase(updateRules, (state, action) => {
    updateRulesReferences(
      state.functions.entities,
      action.payload.references?.globalFunctions ?? {},
      functionsReferencesAdapter.addReference,
      functionsReferencesAdapter.removeReference,
      state.functions,
    );
  });
}

export function registerStopkeywords(
  builder: ActionReducerMapBuilder<{
    functions: EntityState<Function, string>;
  }>,
) {
  builder.addCase(deleteStopKeywords, (state, action) => {
    deleteStopKeywordsReferences(
      state.functions.entities,
      action.payload.id,
      functionsReferencesAdapter.removeReference,
      state.functions,
    );
  });
}

export function registerMatchers(
  builder: ActionReducerMapBuilder<{
    functions: EntityState<Function, string>;
  }>,
) {
  // Topic is updated or created
  builder.addMatcher(isTopicReferencesFunction, (state, action) => {
    handleTopicReferenceUpdates(
      state.functions.entities,
      action.payload.references?.globalFunctions ?? {},
      action.payload.id,
      functionsReferencesAdapter.addReference,
      functionsReferencesAdapter.removeReference,
      state.functions,
    );
  });

  // Step flow is created or updated
  builder.addMatcher(isStepReferencesFunction, (state, action) => {
    handleStepReferenceUpdates(
      state.functions.entities,
      action.payload.step.references.globalFunctions ?? {},
      action.payload.flowId,
      action.payload.step.id,
      functionsReferencesAdapter.addReference,
      functionsReferencesAdapter.removeReference,
      state.functions,
    );
  });

  // Stop keyword is created or updated
  builder.addMatcher(isStopKeywordReferencesFunction, (state, action) => {
    updateStopKeywordsReferences(
      state.functions.entities,
      action.payload.id,
      action.payload.references.globalFunctions ?? {},
      functionsReferencesAdapter.addReference,
      functionsReferencesAdapter.removeReference,
      state.functions,
    );
  });
}
